import { Alert, Button, Card, CardBody, Input } from '@foundation/ui'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import styles from './login.module.css'
import { api } from '@/lib/api'

export const Route = createFileRoute('/reset-password')({
  component: ResetPasswordPage,
})

interface FormErrors {
  password?: string
  confirmPassword?: string
}

function ResetPasswordPage() {
  const navigate = useNavigate()
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [errors, setErrors] = useState<FormErrors>({})
  const [apiError, setApiError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [tokenError, setTokenError] = useState<string | null>(null)

  const token = new URLSearchParams(window.location.search).get('token')

  if (!token && !tokenError) {
    setTokenError('Missing reset token. Please check your email link.')
  }

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {}

    if (!password) {
      newErrors.password = 'Password is required'
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    } else if (!/[A-Z]/.test(password)) {
      newErrors.password = 'Password must contain at least one uppercase letter'
    } else if (!/[a-z]/.test(password)) {
      newErrors.password = 'Password must contain at least one lowercase letter'
    } else if (!/[0-9]/.test(password)) {
      newErrors.password = 'Password must contain at least one number'
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password'
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e: React.FormEvent): void => {
    e.preventDefault()
    setApiError(null)

    if (!token) {
      setApiError('Missing reset token. Please request a new password reset.')
      return
    }

    if (!validateForm()) return

    setIsLoading(true)

    api
      .resetPassword({ token, new_password: password })
      .then(() => {
        setIsSuccess(true)
      })
      .catch((err: unknown) => {
        setApiError(err instanceof Error ? err.message : 'Failed to reset password')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  if (tokenError) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>🔄</span>
              <span className={styles.logoText}>BounceBack</span>
            </div>
            <h2 className={styles.title}>Invalid link</h2>
            <p className={styles.subtitle}>{tokenError}</p>
            <Alert variant="danger" className={styles.alert}>
              The password reset link is invalid or missing a token.
            </Alert>
            <p className={styles.footer}>
              <Link to="/forgot-password">Request a new reset link</Link>
            </p>
          </CardBody>
        </Card>
      </main>
    )
  }

  if (isSuccess) {
    return (
      <main className={styles.container}>
        <Card className={styles.card}>
          <CardBody>
            <div className={styles.logo}>
              <span className={styles.logoIcon}>🔄</span>
              <span className={styles.logoText}>BounceBack</span>
            </div>
            <h2 className={styles.title}>Password reset</h2>
            <p className={styles.subtitle}>
              Your password has been successfully updated. You can now sign in with your new
              password.
            </p>
            <Alert variant="success" className={styles.alert}>
              Password changed successfully.
            </Alert>
            <div className={styles.actions}>
              <Button
                variant="primary"
                className={styles.fullWidth}
                onClick={() => void navigate({ to: '/login' })}
              >
                Go to Login
              </Button>
            </div>
          </CardBody>
        </Card>
      </main>
    )
  }

  return (
    <main className={styles.container}>
      <Card className={styles.card}>
        <CardBody>
          <div className={styles.logo}>
            <span className={styles.logoIcon}>🔄</span>
            <span className={styles.logoText}>BounceBack</span>
          </div>
          <h2 className={styles.title}>Set new password</h2>
          <p className={styles.subtitle}>Enter your new password below.</p>

          {apiError && (
            <Alert variant="danger" className={styles.alert}>
              {apiError}
            </Alert>
          )}

          <form className={styles.form} onSubmit={handleSubmit}>
            <Input
              label="New Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              autoComplete="new-password"
              {...(errors.password && { error: errors.password })}
              required
            />

            <Input
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm your new password"
              autoComplete="new-password"
              {...(errors.confirmPassword && { error: errors.confirmPassword })}
              required
            />

            <Button
              type="submit"
              variant="primary"
              loading={isLoading}
              className={styles.fullWidth}
            >
              Reset password
            </Button>
          </form>

          <p className={styles.footer}>
            Remember your password? <Link to="/login">Sign in</Link>
          </p>
        </CardBody>
      </Card>
    </main>
  )
}
